import React, { useState } from "react";
import { Loader2, Upload, FileText, Sparkles, CheckCircle2, AlertCircle, AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";
import AIAuditBadge from "@/components/AIAuditBadge";
import { toast } from "sonner";

const SEVERITY = {
  info: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", icon: AlertCircle, label: "Info" },
  warning: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle, label: "Varning" },
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: AlertCircle, label: "Kritisk" },
};

export default function DrawingReview() {
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(png|jpe?g|webp|pdf)$/i.test(f.name)) { toast.warning("PNG, JPG, WEBP eller PDF"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.warning("Max 15 MB"); return; }
    setFile(f);
    setResult(null);
  };

  const review = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/drawing-review", fd, {
        params: { hint },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Granskning misslyckades");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <Sparkles className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bygglovsguide</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Ritningsgranskning</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600 max-w-2xl">Ladda upp en bygglovsritning så får du AI-feedback mot BBR/PBL. Drivs av Gemini 3 Pro (multimodal).</p>

      <div className="mt-8 bg-white border border-zinc-200 rounded-sm p-6">
        {!file ? (
          <label data-testid="drawing-dropzone"
            className="block border-2 border-dashed border-zinc-300 rounded-sm p-12 text-center cursor-pointer hover:border-blue-400 transition">
            <Upload className="w-10 h-10 mx-auto text-zinc-400 stroke-[1.5]" />
            <div className="mt-3 text-sm font-medium text-zinc-900">Klicka för att ladda upp ritning</div>
            <div className="text-xs text-zinc-500">PNG, JPG, WEBP eller PDF · max 15 MB</div>
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={onPick} data-testid="drawing-input" className="hidden" />
          </label>
        ) : (
          <div>
            <div className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
              <FileText className="w-5 h-5 text-blue-700" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{file.name}</div>
                <div className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button onClick={() => { setFile(null); setResult(null); }} className="p-1 hover:bg-zinc-100 rounded-sm">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Kontext (valfritt)</label>
              <input data-testid="drawing-hint" value={hint} onChange={(e) => setHint(e.target.value)}
                placeholder="t.ex. Planritning för attefallshus 28 kvm"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <button onClick={review} disabled={loading} data-testid="drawing-submit"
              className="mt-4 w-full bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-sm font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> AI granskar ritningen (kan ta 30-60 s)...</> : "Granska ritning"}
            </button>
          </div>
        )}
      </div>

      {result && (
        <div className="mt-6 space-y-4" data-testid="drawing-result">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Identifierad ritningstyp</div>
                <div className="mt-1 text-xl font-medium capitalize" style={{ fontFamily: "Outfit" }}>{result.drawing_type || "okänd"}</div>
              </div>
              {typeof result.overall_score === "number" && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Helhetsbetyg</div>
                  <div className={`text-4xl font-semibold ${
                    result.overall_score >= 80 ? "text-emerald-600" :
                    result.overall_score >= 60 ? "text-amber-600" : "text-red-600"
                  }`} style={{ fontFamily: "Outfit" }}>
                    {result.overall_score}<span className="text-base text-zinc-400">/100</span>
                  </div>
                </div>
              )}
            </div>
            {result.summary && <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{result.summary}</p>}
          </div>

          {result.findings?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-sm p-6">
              <h3 className="font-medium mb-4" style={{ fontFamily: "Outfit" }}>Iakttagelser</h3>
              <ul className="space-y-3">
                {result.findings.map((f, i) => {
                  const s = SEVERITY[f.severity] || SEVERITY.info;
                  return (
                    <li key={i} className={`flex gap-3 p-3 border rounded-sm ${s.bg} ${s.border}`}>
                      <s.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.color}`} />
                      <div className="min-w-0">
                        <div className={`text-xs uppercase tracking-wider font-semibold ${s.color}`}>{f.area || s.label}</div>
                        <div className="text-sm text-zinc-800 mt-0.5">{f.text}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {result.missing_information?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-sm p-6">
              <h3 className="font-medium mb-3" style={{ fontFamily: "Outfit" }}>Saknad information</h3>
              <ul className="space-y-1.5 text-sm text-zinc-700">
                {result.missing_information.map((s, i) => <li key={i} className="flex gap-2">• {s}</li>)}
              </ul>
            </div>
          )}

          {result.suggestions?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-sm p-6">
              <h3 className="font-medium mb-3" style={{ fontFamily: "Outfit" }}>Förbättringsförslag</h3>
              <ul className="space-y-1.5 text-sm text-zinc-700">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /> {s}</li>
                ))}
              </ul>
            </div>
          )}

          {result.audit_id && (
            <div className="flex justify-end">
              <AIAuditBadge auditId={result.audit_id} kind="drawing_review" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
